<template>
    <button v-if="embedUrl"
            type="button"
            :class="buttonClasses"
            aria-label="Watch movement demo"
            title="Watch movement demo"
            @click.stop="handleOpen">
        <Icon icon="play" pack="fas" :class="label ? 'mr-2' : null"/>
        <span v-if="label">{{ label }}</span>
    </button>

    <Teleport to="body">
        <div v-if="videoOpen"
             class="fixed inset-0 z-[90] flex flex-col bg-black md:items-center md:justify-center md:bg-black/85 md:p-4"
             @click="handleClose">
            <div class="flex h-[100dvh] w-full flex-col overflow-hidden bg-black shadow-2xl md:h-auto md:max-h-[calc(100vh-2rem)] md:max-w-2xl md:rounded md:border md:border-neutral-700 md:bg-neutral-950"
                 @click.stop>
                <div class="flex shrink-0 justify-end bg-black px-3 py-2 md:hidden">
                    <button type="button"
                            class="flex h-11 w-11 shrink-0 items-center justify-center rounded border border-neutral-700 text-neutral-200 hover:border-neutral-500 hover:text-white"
                            aria-label="Close video"
                            @click.stop="handleClose">
                        <Icon icon="times"/>
                    </button>
                </div>
                <div class="hidden shrink-0 items-center justify-between gap-3 border-b border-neutral-800 bg-neutral-950 px-3 py-2 md:flex md:px-4 md:py-3">
                    <div class="min-w-0">
                        <div class="truncate font-semibold">{{ movementName }}</div>
                    </div>
                    <button type="button"
                            class="flex h-9 w-9 shrink-0 items-center justify-center rounded border border-neutral-700 text-neutral-300 hover:border-neutral-500 hover:text-white"
                            aria-label="Close video"
                            @click="handleClose">
                        <Icon icon="times"/>
                    </button>
                </div>
                <div class="relative flex min-h-0 flex-1 items-center justify-center bg-black md:flex-none">
                    <div :class="playerFrameClasses">
                        <iframe class="absolute inset-0 h-full w-full"
                                :src="embedUrlWithPlayback"
                                :title="`${movementName} demo video`"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowfullscreen
                        />
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import {defineComponent} from 'vue';

export default defineComponent({
  name: 'MovementDemoVideoButton',
  computed: {
    embedUrl() {
      return this.youtubeEmbed?.url || null;
    },
    embedUrlWithPlayback() {
      if (!this.embedUrl) {
        return null;
      }

      const playbackParams = new URLSearchParams({
        autoplay: '1',
        rel: '0',
      });

      if (this.youtubeEmbed.startSeconds !== null) {
        playbackParams.set('start', this.youtubeEmbed.startSeconds);
      }

      return `${this.embedUrl}?${playbackParams.toString()}`;
    },
    movementName() {
      return this.movement?.name || 'Movement';
    },
    buttonClasses() {
      return this.label
        ? 'inline-flex h-8 shrink-0 items-center justify-center rounded border border-brand-primary-500 px-3 text-sm font-semibold text-brand-primary-500 transition hover:border-brand-primary-300 hover:text-brand-primary-300'
        : 'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-brand-primary-500 transition hover:bg-brand-primary-500/10 hover:text-brand-primary-300';
    },
    playerFrameClasses() {
      return this.youtubeEmbed?.isShort
        ? 'relative aspect-[9/16] h-full max-h-full w-auto max-w-full md:h-[80vh] md:max-h-[720px]'
        : 'relative aspect-video w-full max-w-full';
    },
    youtubeEmbed() {
      return this.toYouTubeEmbed(this.movement?.demoVideoUrl);
    },
  },
  data() {
    return {
      videoOpen: false,
    };
  },
  methods: {
    handleClose() {
      this.videoOpen = false;
    },
    handleOpen() {
      this.videoOpen = true;
    },
    toYouTubeEmbed(value) {
      if (!value) {
        return null;
      }

      try {
        const url = new URL(value);
        const host = url.hostname.toLowerCase().replace(/^www\./, '');
        const pathParts = url.pathname.split('/').filter(Boolean);
        const isShort = pathParts[0] === 'shorts';
        const timestamp = url.searchParams.get('t') || url.searchParams.get('start');
        let videoId = null;

        if (host === 'youtu.be') {
          videoId = pathParts[0];
        } else if (host === 'youtube.com' || host.endsWith('.youtube.com')) {
          if (url.pathname === '/watch') {
            videoId = url.searchParams.get('v');
          } else if (['embed', 'shorts'].includes(pathParts[0])) {
            videoId = pathParts[1];
          }
        }

        return videoId
          ? {
            isShort,
            startSeconds: this.youtubeTimestampToSeconds(timestamp),
            url: `https://www.youtube.com/embed/${videoId}`,
          }
          : null;
      } catch (error) {
        return null;
      }
    },
    youtubeTimestampToSeconds(value) {
      if (value === null || value === '') {
        return null;
      }

      if (/^\d+$/.test(value)) {
        return Number(value);
      }

      const match = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);

      if (!match) {
        return null;
      }

      return (Number(match[1] || 0) * 3600)
        + (Number(match[2] || 0) * 60)
        + Number(match[3] || 0);
    },
  },
  props: {
    label: {
      default: '',
      required: false,
      type: String,
    },
    movement: {
      default: null,
      required: false,
    },
  },
});
</script>
